import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface DayOutcome {
  date: string
  outcomes: {
    medicationId: string | undefined
    medicationName: string
    timeSlot: string
    outcome: string
    completedAt: number | undefined
  }[]
}

interface MedicationCalendarProps {
  adherenceHistory: DayOutcome[]
  onDayClick: (date: string) => void
}

/**
 * Calendar component showing medication adherence history
 */
export function MedicationCalendar({ adherenceHistory, onDayClick }: MedicationCalendarProps) {
  const { t } = useTranslation()
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const containerStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
    marginBottom: '1.5rem',
  }

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  }

  const monthStyles: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
  }

  const navButtonStyles: React.CSSProperties = {
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#6b7280',
    minWidth: '44px',
    minHeight: '44px',
  }

  const gridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px',
  }

  const dayHeaderStyles: React.CSSProperties = {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#9ca3af',
    padding: '0.5rem 0',
  }

  const dayCellStyles = (isCurrentMonth: boolean, hasData: boolean): React.CSSProperties => ({
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: hasData ? 'pointer' : 'default',
    backgroundColor: hasData ? '#f9fafb' : 'transparent',
    opacity: isCurrentMonth ? 1 : 0.3,
    minHeight: '44px',
  })

  const dayNumberStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 500,
    color: '#1a1a1a',
  }

  const legendStyles: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #f3f4f6',
    justifyContent: 'center',
  }

  const legendItemStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '12px',
    color: '#6b7280',
  }

  const getOutcomeIcon = (outcomes: DayOutcome['outcomes']): string => {
    if (!outcomes || outcomes.length === 0) return ''

    // If any outcome is taken or taking_now, show green check
    if (outcomes.some(o => o.outcome === 'taken' || o.outcome === 'taking_now')) {
      return '✓'
    }
    // If any outcome is will_take_later, show yellow clock
    if (outcomes.some(o => o.outcome === 'will_take_later')) {
      return '⏰'
    }
    // If any outcome is no_answer or missed, show red X
    if (outcomes.some(o => o.outcome === 'no_answer' || o.outcome === 'missed')) {
      return '✗'
    }
    // Pending
    return '•'
  }

  const getOutcomeColor = (outcomes: DayOutcome['outcomes']): string => {
    if (!outcomes || outcomes.length === 0) return '#9ca3af'

    if (outcomes.some(o => o.outcome === 'taken' || o.outcome === 'taking_now')) {
      return '#22c55e'
    }
    if (outcomes.some(o => o.outcome === 'will_take_later')) {
      return '#eab308'
    }
    if (outcomes.some(o => o.outcome === 'no_answer' || o.outcome === 'missed')) {
      return '#ef4444'
    }
    return '#9ca3af'
  }

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startDayOfWeek = firstDay.getDay()

    const days: { date: Date; isCurrentMonth: boolean }[] = []

    // Add days from previous month
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i)
      days.push({ date: prevDate, isCurrentMonth: false })
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true })
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false })
    }

    return days
  }

  const formatDateString = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  const getDayData = (date: Date): DayOutcome | undefined => {
    const dateStr = formatDateString(date)
    return adherenceHistory.find(d => d.date === dateStr)
  }

  const days = getDaysInMonth(currentMonth)
  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const monthName = currentMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <button style={navButtonStyles} onClick={goToPrevMonth}>
          ←
        </button>
        <span style={monthStyles}>{monthName}</span>
        <button style={navButtonStyles} onClick={goToNextMonth}>
          →
        </button>
      </div>

      <div style={gridStyles}>
        {dayHeaders.map((day, i) => (
          <div key={i} style={dayHeaderStyles}>
            {day}
          </div>
        ))}

        {days.map((day, i) => {
          const dayData = getDayData(day.date)
          const hasData = !!dayData

          return (
            <div
              key={i}
              style={dayCellStyles(day.isCurrentMonth, hasData)}
              onClick={() => hasData && onDayClick(formatDateString(day.date))}
            >
              <span style={dayNumberStyles}>{day.date.getDate()}</span>
              {hasData && (
                <span style={{ fontSize: '14px', color: getOutcomeColor(dayData.outcomes) }}>
                  {getOutcomeIcon(dayData.outcomes)}
                </span>
              )}
            </div>
          )
        })}
      </div>

      <div style={legendStyles}>
        <div style={legendItemStyles}>
          <span style={{ color: '#22c55e' }}>✓</span>
          {t('medications.taken', 'Taken')}
        </div>
        <div style={legendItemStyles}>
          <span style={{ color: '#eab308' }}>⏰</span>
          {t('medications.takingLater', 'Taking Later')}
        </div>
        <div style={legendItemStyles}>
          <span style={{ color: '#ef4444' }}>✗</span>
          {t('medications.missed', 'Missed')}
        </div>
        <div style={legendItemStyles}>
          <span style={{ color: '#9ca3af' }}>•</span>
          {t('medications.pending', 'Pending')}
        </div>
      </div>
    </div>
  )
}
